import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { Layout, ProgressBar, TextStyle } from "@shopify/polaris";
import { useApolloClient, useQuery } from "@apollo/react-hooks";

import Moment from "moment";

// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";

import constant from "lib/constant/constant";
import { baseHelper } from "lib/helpers";
import { useBatchExport } from "lib/hooks";
import { PrivateContext } from "lib/context";
import { Banner } from "lib/components";

import {
  GET_ORDER_REPORT_SETTING,
  GET_PRODUCT_REPORT_SETTING,
  GET_PAYMENT_REPORT_SETTING,
  GET_REPORT_DATA,
} from "app/setup/apollo/queries";

import reportHelper from "app/dashboard/modules/reportHelper";

const ExportReport = () => {
  const data = useContext(PrivateContext);
  const { cms, currentUser } = data;
  const client = useApolloClient();
  const hasStartedRef = useRef(false);

  const isVendor = baseHelper.isVendor(currentUser);
  const isXhawiVendor = baseHelper.isVendorXhawi(currentUser, isVendor);
  const isXhawi = baseHelper.isOperatorXhawi(currentUser);
  const { reportType } = baseHelper.queryParamsFromLocation(data);
  const isEshopReport = reportType === "eshop";

  const {
    data: getOrderReportSettingData,
    error: getOrderReportSettingError,
    loading: getOrderReportSettingLoading,
  } = useQuery(GET_ORDER_REPORT_SETTING);

  const {
    data: getProductReportSettingData,
    error: getProductReportSettingError,
    loading: getProductReportSettingLoading,
  } = useQuery(GET_PRODUCT_REPORT_SETTING);

  const {
    data: getPaymentReportSettingData,
    error: getPaymentReportSettingError,
    loading: getPaymentReportSettingLoading,
  } = useQuery(GET_PAYMENT_REPORT_SETTING);

  const [banner, setBanner] = useState({
    title: "",
    status: "",
    isOpen: false,
  });

  const showErrorBanner = useCallback((title) => {
    setBanner({ title, status: constant.CRITICAL, isOpen: true });
    setTimeout(() => window.close(), 2000);
  }, []);

  const fetchPage = useCallback(
    (page, filters) =>
      client
        .query({
          query: GET_REPORT_DATA,
          variables: { input: { ...filters, page } },
          fetchPolicy: "network-only",
        })
        .then((res) => {
          const response = res.data.getReportData;
          if (response.status !== constant.OK) {
            throw new Error(response.error || cms("common.message.error.noOrder"));
          }
          return { rows: response.data || [], hasMore: response.hasMore, totalCount: response.totalCount };
        }),
    [client, cms]
  );

  const mapRows = useCallback(
    (rows) => {
      if (isEshopReport) {
        return reportHelper.eshopReportCSVData(rows);
      }
      const orderReportSettingResponse = baseHelper.getResponseData(
        getOrderReportSettingData,
        constant.gql.GET_ORDER_REPORT_SETTING
      );
      const paymentReportSettingResponse = baseHelper.getResponseData(
        getPaymentReportSettingData,
        constant.gql.GET_PAYMENT_REPORT_SETTING
      );
      const productReportSettingResponse = baseHelper.getResponseData(
        getProductReportSettingData,
        constant.gql.GET_PRODUCT_REPORT_SETTING
      );
      return reportHelper.reportCSVData(
        rows,
        orderReportSettingResponse,
        paymentReportSettingResponse,
        productReportSettingResponse,
        true,
        false,
        isVendor,
        isXhawiVendor,
        isXhawi
      );
    },
    [
      isEshopReport,
      getOrderReportSettingData,
      getPaymentReportSettingData,
      getProductReportSettingData,
      isVendor,
      isXhawiVendor,
      isXhawi,
    ]
  );

  const getFileName = useCallback(() => {
    const date = Moment(new Date(), constant.FORMATDATE).format(constant.LL);
    if (isEshopReport) {
      return `Order Level Report ${date}.csv`;
    }
    if (isXhawiVendor) {
      return `${currentUser?.brandName} ${constant.CONSOLIDATED_REPORT} ${date}.csv`;
    }
    return `${constant.CONSOLIDATED_REPORT} ${date}.csv`;
  }, [isEshopReport, isXhawiVendor, currentUser]);

  const onError = useCallback((err) => showErrorBanner(err.message || cms("common.message.error.noOrder")), [
    showErrorBanner,
    cms,
  ]);

  const { status: exportStatus, fetchedCount, totalCount, progress, start } = useBatchExport({
    fetchPage,
    mapRows,
    getFileName,
    onError,
  });

  useEffect(() => {
    if (exportStatus === "empty") {
      showErrorBanner(cms("common.message.error.noOrder"));
    }
    if (exportStatus === "done") {
      setTimeout(() => window.close(), 500);
    }
  }, [exportStatus, showErrorBanner, cms]);

  useEffect(() => {
    const settingsLoading =
      getOrderReportSettingLoading || getProductReportSettingLoading || getPaymentReportSettingLoading;
    if (settingsLoading || hasStartedRef.current) {
      return;
    }

    const settingsError = getOrderReportSettingError || getProductReportSettingError || getPaymentReportSettingError;
    if (settingsError) {
      showErrorBanner(settingsError.message || cms("common.message.error.noOrder"));
      return;
    }

    hasStartedRef.current = true;
    const { status, fulfillmentStatus, vendor, accountingStatus } = baseHelper.queryParamsFromLocation(data);
    const filters = {};
    if (status && status.length) {
      filters.status = status.split(",");
    }
    if (fulfillmentStatus && fulfillmentStatus.length) {
      filters.fulfillmentStatus = fulfillmentStatus.split(",");
    }
    if (vendor && vendor.length) {
      filters.vendor = vendor.split(",");
    }
    if (accountingStatus && accountingStatus.length) {
      filters.accountingStatus = accountingStatus.split(",");
    }
    start(filters);
  }, [
    data,
    start,
    cms,
    showErrorBanner,
    getOrderReportSettingLoading,
    getProductReportSettingLoading,
    getPaymentReportSettingLoading,
    getOrderReportSettingError,
    getProductReportSettingError,
    getPaymentReportSettingError,
  ]);

  return (
    <Layout>
      <Layout.Section>
        {exportStatus === "loading" && (
          <div className="fullPageProgress">
            <ProgressBar progress={progress} size="medium" />
            <br />
            <TextStyle variation="subdued">
              {totalCount ? `Exporting ${fetchedCount} of ${totalCount} records...` : "Preparing export..."}
            </TextStyle>
          </div>
        )}
        {banner.isOpen && (
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
          />
        )}
      </Layout.Section>
    </Layout>
  );
};

export default withFeature(withErrorBoundary(ExportReport), { feature: constant.EXPORT_REPORT });
